// Settings → Sign-in and security (/me/settings/security): passkeys, the
// address codes go to, and Google.
//
// Deleting a passkey names it and, for the last one, says the next sign-in is
// by code. Failures appear under what was pressed.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/cloud-i18n";
import { m as common } from "@trameria/i18n";
import {
  Button,
  confirmAction,
  ErrorLine,
  iconOf,
  SettingsBox,
  type SettingsItem,
  SettingsRow,
  Skeleton,
  useApi,
  useFormat,
} from "@trameria/ui-core";
import { useState } from "react";
import { linkGoogle } from "../auth/client";
import {
  accountQuery,
  authConfigQuery,
  type Passkey,
  passkeysQuery,
  useAddPasskey,
  useDeletePasskey,
} from "../queries";

export const securitySettings: SettingsItem = {
  id: "security",
  order: 40,
  label: () => m.web_settings_security(),
  icon: iconOf("lock"),
  description: () => m.web_settings_security_description(),
  sections: [
    { id: "passkeys", label: () => m.web_settings_security_passkeys() },
    { id: "email", label: () => m.web_settings_security_email() },
    { id: "google", label: () => m.web_settings_security_google() },
  ],
  Component: SecuritySettings,
};

function SecuritySettings() {
  const api = useApi();
  const account = useQuery(accountQuery(api));
  const config = useQuery(authConfigQuery(api));
  // One box, as the old web: the passkeys, the address and Google are the
  // ways of signing in to one account.
  return (
    <SettingsBox>
      <Passkeys />
      <SettingsRow
        id="email"
        label={m.web_settings_security_email()}
        description={m.web_settings_security_email_note()}
      >
        <span className="min-w-0 truncate text-[13px]">
          {account.data?.email}
        </span>
      </SettingsRow>
      <SettingsRow
        id="google"
        label={m.web_settings_security_google()}
        description={m.web_settings_security_google_note()}
      >
        {!config.data?.google ? (
          <span className="text-[13px] text-faint">
            {m.web_settings_security_google_not_configured()}
          </span>
        ) : account.data?.google ? (
          <span className="text-[13px]">
            {m.web_settings_security_google_linked()}
          </span>
        ) : (
          <LinkGoogle />
        )}
      </SettingsRow>
    </SettingsBox>
  );
}

function LinkGoogle() {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex flex-col items-end">
      <Button
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setFailed(false);
          // Success leaves for Google; coming back means it did not start.
          void linkGoogle("/me/settings/security").then(({ error }) => {
            setBusy(false);
            if (error) setFailed(true);
          });
        }}
      >
        {m.web_settings_security_google_link()}
      </Button>
      {failed && (
        <ErrorLine>{m.web_settings_security_google_link_failed()}</ErrorLine>
      )}
    </div>
  );
}

function Passkeys() {
  const api = useApi();
  const format = useFormat();
  const passkeys = useQuery(passkeysQuery(api));
  const add = useAddPasskey();
  const del = useDeletePasskey();
  const [deleting, setDeleting] = useState<string | null>(null);
  const nameOf = (p: Passkey) => p.name || m.web_settings_security_unnamed();

  async function remove(p: Passkey) {
    const last = (passkeys.data ?? []).length === 1;
    const ok = await confirmAction({
      title: m.web_settings_security_delete_title({ name: nameOf(p) }),
      body: last
        ? m.web_settings_security_delete_last_body()
        : m.web_settings_security_delete_body(),
      action: common.common_delete(),
      danger: true,
    });
    if (!ok) return;
    setDeleting(nameOf(p));
    del.mutate(p.id);
  }

  return (
    <div id="passkeys" className="scroll-mt-4 px-4 py-3">
      <div className="text-[13px] leading-[1.5] text-fg">
        {m.web_settings_security_passkeys()}
      </div>
      <div className="mt-px text-[11.5px] leading-[1.5] text-faint">
        {m.web_settings_security_passkeys_note()}
      </div>
      {/* The failed delete takes the row with it: its line goes on top. */}
      {del.isError && deleting && (
        <ErrorLine>
          {m.web_settings_security_delete_failed({ name: deleting })}
        </ErrorLine>
      )}
      <div className="mt-2.5 flex flex-col">
        {passkeys.isError ? (
          <div className="flex items-center gap-3 text-[13px] text-muted">
            {m.web_load_failed()}
            <Button onClick={() => void passkeys.refetch()}>
              {m.web_retry()}
            </Button>
          </div>
        ) : !passkeys.data ? (
          <Skeleton lines={[50, 40]} />
        ) : (
          passkeys.data.map((p) => (
            <div
              key={p.id}
              className="flex min-h-9 items-center justify-between gap-4 text-[13px]"
            >
              <span className="min-w-0 truncate">
                {nameOf(p)}
                <span className="ml-2 text-[11.5px] text-faint tabular-nums">
                  {m.web_settings_security_registered_on({
                    date: format.day(p.createdAt),
                  })}
                </span>
              </span>
              <Button
                variant="ghost"
                aria-label={m.web_settings_security_delete_named({
                  name: nameOf(p),
                })}
                onClick={() => void remove(p)}
              >
                {common.common_delete()}
              </Button>
            </div>
          ))
        )}
      </div>
      <div className="mt-2">
        <Button disabled={add.isPending} onClick={() => add.mutate()}>
          {m.web_settings_security_add()}
        </Button>
        {add.isError && (
          <ErrorLine>{m.web_settings_security_add_failed()}</ErrorLine>
        )}
      </div>
    </div>
  );
}
